import { createAsyncThunk } from "@reduxjs/toolkit";

import { getLang } from "containers/authentication/redux/selector";
import { showNotificationActionCreator } from "containers/notifications/actionCreator";
import { NOTIFICATION_LEVEL_SUCCESS } from "containers/notifications/actionLevels";
import { NOTIFICATION_SUCCESS } from "containers/notifications/actionTypes";
import * as lang from "lang";
import TLang from "model/application/Lang";
import {
  ajaxRequestAction,
  ajaxSuccessAction,
} from "redux/actionCreators/ajaxActionCreator";
import { treatErrorNotification } from "redux/actions/appActions";
import IRootState from "redux/store/model";

type TCreateMutationThunkConfig<TArg, TReturn> = {
  typePrefix: string;
  apiCall: (arg: TArg) => Promise<TReturn>;
  getSuccessMessage?: (params: {
    arg: TArg;
    result: TReturn;
    lang: TLang;
  }) => string;
  actionName: string;
};

export type TMutationError = {
  message: string;
  errorStatus?: string;
};

const toMutationError = (error: unknown): TMutationError => ({
  message: error instanceof Error ? error.message : String(error),
  errorStatus:
    typeof error === "object" && error && "errorStatus" in error
      ? String(error.errorStatus)
      : undefined,
});

/**
 * Async thunk for a create/update/delete call.
 * Dispatches the ajax counter, an optional success toast, and `treatErrorNotification` on failure.
 * Never throws: awaiting the dispatch yields the fulfilled or rejected action, so check `thunk.fulfilled.match(result)` instead of the return value.
 */
export function createMutationThunk<TArg, TReturn>(
  config: TCreateMutationThunkConfig<TArg, TReturn>,
) {
  return createAsyncThunk<
    TReturn,
    TArg,
    { state: IRootState; rejectValue: TMutationError }
  >(config.typePrefix, async (arg, api) => {
    const currLang = (lang as unknown as Record<string, TLang>)[
      getLang(api.getState())
    ];
    api.dispatch(ajaxRequestAction());

    try {
      const result = await config.apiCall(arg);
      api.dispatch(ajaxSuccessAction());
      if (config.getSuccessMessage) {
        api.dispatch(
          showNotificationActionCreator(
            NOTIFICATION_SUCCESS,
            NOTIFICATION_LEVEL_SUCCESS,
            config.getSuccessMessage({ arg, result, lang: currLang }),
          ),
        );
      }
      return result;
    } catch (error) {
      treatErrorNotification(
        api.dispatch,
        config.actionName,
        error,
        null,
        currLang,
      );
      return api.rejectWithValue(toMutationError(error));
    }
  });
}
